import { Alert, Autocomplete, Box, Button, Card, CardContent, CardHeader, Chip, Link, Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import HeaderTitle from "./HeaderTitle";

interface AttorneyOption {
  id: number;
  name: string;
  company?: string;
}

interface Preferences {
  attorneys: AttorneyOption[];
  focusStates: string[];
  stateOptions: { code: string; name: string }[];
}

export default function ContactFocusPreferences({ contactId, isActive }: { contactId: number; isActive: boolean }) {
  const [preferences, setPreferences] = useState<Preferences>();
  const [loadError, setLoadError] = useState(false);
  const [editAttorneys, setEditAttorneys] = useState(false);
  const [editStates, setEditStates] = useState(false);
  const [attorneys, setAttorneys] = useState<AttorneyOption[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [options, setOptions] = useState<AttorneyOption[]>([]);
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [saving, setSaving] = useState<"attorneys" | "focusStates" | null>(null);
  const [saveError, setSaveError] = useState<"attorneys" | "focusStates" | null>(null);
  const [reload, setReload] = useState(0);
  const api = `/api/clientphonebook/contactPreferences/${contactId}`;

  useEffect(() => {
    const controller = new AbortController();
    setLoadError(false);
    axios.get<Preferences>(api, { signal: controller.signal })
      .then(({ data }) => setPreferences(data))
      .catch((error) => { if (!axios.isCancel(error)) setLoadError(true); });
    return () => controller.abort();
  }, [api, reload]);

  useEffect(() => {
    if (!editAttorneys) return;
    const controller = new AbortController();
    setSearching(true);
    setSearchError(false);
    const timer = window.setTimeout(() => {
      axios.get<AttorneyOption[]>(`${api}/attorneyOptions`, { params: { search }, signal: controller.signal })
        .then(({ data }) => setOptions(data))
        .catch((error) => { if (!axios.isCancel(error)) setSearchError(true); })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 250);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [api, editAttorneys, search]);

  const save = async (section: "attorneys" | "focusStates") => {
    if (!preferences || saving) return;
    setSaving(section);
    setSaveError(null);
    try {
      await axios.put(`${api}/${section}`, section === "attorneys" ? attorneys.map((a) => a.id) : states);
      setPreferences((current) => current && {
        ...current,
        ...(section === "attorneys" ? { attorneys: [...attorneys] } : { focusStates: [...states].sort() })
      });
      if (section === "attorneys") setEditAttorneys(false);
      else setEditStates(false);
    } catch {
      setSaveError(section);
    } finally {
      setSaving(null);
    }
  };

  const actions = (section: "attorneys" | "focusStates", cancel: () => void) => (
    <>
      {saveError === section && <Alert severity="error">Unable to save. Please try again.</Alert>}
      <Stack direction="row" spacing={1.5} justifyContent="flex-end">
        <Button variant="outlined" disabled={Boolean(saving)} onClick={() => { setSaveError(null); cancel(); }}>Cancel</Button>
        <Button variant="contained" disabled={Boolean(saving) || !isActive} onClick={() => save(section)}>
          {saving === section ? "Saving..." : "Save"}
        </Button>
      </Stack>
    </>
  );

  const loading = loadError
    ? <Alert severity="error" action={<Button onClick={() => setReload((value) => value + 1)}>Retry</Button>}>Unable to load preferences.</Alert>
    : <Typography variant="body2" color="text.secondary">Loading...</Typography>;

  // Include selected attorneys even when they are outside the current search results.
  const attorneyOptions = [...attorneys, ...options.filter((option) => !attorneys.some((a) => a.id === option.id))];
  const stateOptions = preferences?.stateOptions || [];
  const getStateName = (code: string) => stateOptions.find((state) => state.code === code)?.name || code;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" }, gap: 3 }}>
      <Card>
        <CardHeader title={<HeaderTitle title="Attorneys" isDisabled={!isActive || !preferences || Boolean(saving)}
          handleEditClick={editAttorneys ? undefined : () => {
            setAttorneys(preferences?.attorneys || []);
            setSearch("");
            setEditAttorneys(true);
          }} />} />
        <CardContent>
          {!preferences ? loading : editAttorneys ? (
            <Stack spacing={2}>
              <Autocomplete multiple filterSelectedOptions options={attorneyOptions} filterOptions={(items) => items}
                value={attorneys} loading={searching} disabled={Boolean(saving)}
                getOptionLabel={(option) => option.name} isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, values) => setAttorneys(values)}
                onInputChange={(_, value) => setSearch(value)}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box><Typography variant="body2">{option.name}</Typography>
                      {option.company && <Typography variant="caption" color="text.secondary">{option.company}</Typography>}
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} label="Attorneys" placeholder="Search attorneys" />} />
              {searchError && <Alert severity="error">Unable to search attorneys. Please try again.</Alert>}
              {actions("attorneys", () => setEditAttorneys(false))}
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              {preferences.attorneys.length === 0 && <Typography variant="body2" color="text.secondary">No attorneys selected</Typography>}
              {preferences.attorneys.map((attorney) => (
                <Box key={attorney.id}>
                  <Link component={RouterLink} to={`/phonebook/contact/profile?id=${attorney.id}`} variant="body2">{attorney.name}</Link>
                  {attorney.company && <Typography variant="caption" display="block" color="text.secondary">{attorney.company}</Typography>}
                </Box>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader title={<HeaderTitle title="Focus States" isDisabled={!isActive || !preferences || Boolean(saving)}
          handleEditClick={editStates ? undefined : () => { setStates(preferences?.focusStates || []); setEditStates(true); }} />} />
        <CardContent>
          {!preferences ? loading : editStates ? (
            <Stack spacing={2}>
              <Autocomplete multiple filterSelectedOptions options={stateOptions.map((state) => state.code)} value={states} disabled={Boolean(saving)}
                getOptionLabel={getStateName}
                onChange={(_, values) => setStates(values)}
                renderInput={(params) => <TextField {...params} label="Focus States" />} />
              {actions("focusStates", () => setEditStates(false))}
            </Stack>
          ) : preferences.focusStates.length ? (
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {preferences.focusStates.map((state) => <Chip key={state} label={getStateName(state)} size="small" />)}
            </Stack>
          ) : <Typography variant="body2" color="text.secondary">No focus states selected</Typography>}
        </CardContent>
      </Card>
    </Box>
  );
}
